import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  GROUP_LABEL,
  REPORT_GROUPS,
  presetRange,
  type ReportGroup,
  type ReportKey,
  type ReportMeta,
  type ReportPresetKey,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader, Toolbar } from '../ui/Page';
import { SectionCard } from '../ui/Detail';
import { SearchInput } from '../ui/SearchInput';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/QueryStates';
import { PageSkeleton, Skeleton } from '../ui/Skeleton';
import { IconReports } from '../icons';
import { ReportView, downloadReportCsv } from '../reports/ReportView';
import { PeriodPresets } from '../reports/PeriodPresets';
import { WithOrgClock } from '../ui/WithOrgClock';

/**
 * Reports (PRD v2 §7 V4) — a menu, not a builder: the report catalogue, less any report the
 * caller's role may not open (the server leaves those out of `report.list`). The feature
 * matrix skips the custom report builder on purpose: an owner with four techs does not want a
 * pivot-table editor, they want the six questions they actually ask, already answered.
 *
 * Every report renders through the same view, so the date range, the CSV and the honest
 * dash behave identically whichever one is open.
 */

/*
 * The presets live in `@servwell/shared` now, as a pure function of (key, tz, now).
 *
 * They were built here from `getUTCMonth()`, which is the defect ticket 023 is named for:
 * at 9 PM on 31 July in New York, UTC has already rolled over, so "This month" resolved to
 * 2026-08-01..2026-08-01 — the NEXT month, one day wide — and an owner checking the month's
 * takings after dinner met an empty report. Moving them to shared also puts them next to
 * the range arithmetic the server ranges by, so both ends agree on what "this month" is.
 */

/** One entry of the menu, a catalogue report's button or the Revenue page's link alike. */
const MENU_ITEM =
  'flex min-h-9 w-full items-center rounded-[var(--radius-sm)] px-2 py-1.5 text-left text-sm';
const MENU_IDLE = 'text-muted-foreground hover:bg-surface-2 hover:text-text';

export function ReportsPage() {
  return (
    <WithOrgClock what="your reports" icon={IconReports} title="Reports">
      {(tz) => <ReportsBoard tz={tz} />}
    </WithOrgClock>
  );
}

function ReportsBoard({ tz }: { tz: string }) {
  const trpc = useTRPC();
  const menu = useQuery(trpc.report.list.queryOptions());
  const [key, setKey] = useState<ReportKey>('job-profitability');
  const [preset, setPreset] = useState<ReportPresetKey>('ytd');
  const [search, setSearch] = useState('');

  const range = useMemo(() => presetRange(preset, tz), [preset, tz]);
  const reports = (menu.data ?? []) as ReportMeta[];
  const current = reports.find((r) => r.key === key);

  const result = useQuery({
    ...trpc.report.run.queryOptions({ key, from: range.from, to: range.to }),
    enabled: reports.length > 0,
  });

  if (menu.isPending) return <PageSkeleton />;
  if (menu.isError) {
    return (
      <PageRoot>
        <PageHeader title="Reports" />
        <ErrorState
          icon={IconReports}
          what="your reports"
          onRetry={() => void menu.refetch()}
        />
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <PageHeader
        title="Reports"
        sub="The questions a shop actually asks, already answered. Ask anything else through Connected AI."
      />

      {/* `min-w-0` on the children is load-bearing: a grid/flex item defaults to
          min-width:auto, so the widest report table would stretch the track and take the
          whole page sideways with it. With this, wide tables scroll inside their own
          `.h-scroll` and the page body never does. */}
      <div className="grid gap-4 lg:grid-cols-[240px_1fr] [&>*]:min-w-0">
        {/* The menu. Grouped the way an owner thinks: money, work, customers, parts. */}
        <nav className="space-y-3" aria-label="Reports">
          {REPORT_GROUPS.map((group: ReportGroup) => {
            const rows = reports.filter((r) => r.group === group);
            if (rows.length === 0) return null;
            return (
              <div key={group}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {GROUP_LABEL[group]}
                </p>
                <ul className="space-y-0.5">
                  {/* The Revenue page, first under Money (ops insights Q7). A LINK, not one
                      of the catalogue's buttons: it is a page of its own, with its own
                      address, and not a sixteenth report. */}
                  {group === 'money' ? (
                    <li>
                      <Link to="/reports/revenue" className={`${MENU_ITEM} ${MENU_IDLE}`}>
                        Revenue
                      </Link>
                    </li>
                  ) : null}
                  {rows.map((r) => (
                    <li key={r.key}>
                      <button
                        type="button"
                        onClick={() => {
                          setKey(r.key);
                          setSearch('');
                        }}
                        aria-current={r.key === key ? 'true' : undefined}
                        className={`${MENU_ITEM} ${
                          r.key === key
                            ? 'bg-surface-2 font-semibold text-text'
                            : MENU_IDLE
                        }`}
                      >
                        {r.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <SectionCard
          title={current?.title ?? 'Report'}
          right={
            result.data ? (
              <Button
                variant="ghost"
                onClick={() =>
                  downloadReportCsv(result.data, current?.title ?? 'servwell-report')
                }
              >
                Download CSV
              </Button>
            ) : null
          }
        >
          {current ? (
            <p className="mb-3 text-sm text-muted-foreground">{current.blurb}</p>
          ) : null}

          <Toolbar>
            {current?.ranged === false ? (
              // Saying it out loud beats a greyed-out control the reader has to interpret.
              <span className="text-xs text-muted-foreground">As of today</span>
            ) : (
              <PeriodPresets value={preset} onChange={setPreset} />
            )}
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search these rows…"
              aria-label="Search the report"
            />
          </Toolbar>

          {result.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-20" />
              <Skeleton className="h-48" />
            </div>
          ) : result.isError ? (
            <ErrorState
              icon={IconReports}
              what="this report"
              onRetry={() => void result.refetch()}
            />
          ) : (
            <ReportView
              // Remount per report: the table's local sort/page/filter state is
              // mount-scoped, and two reports sharing a column key (`month`) would
              // inherit each other's sort through the cached instance (Codex 103 C2).
              key={key}
              result={result.data}
              search={search}
              tableId={`report-${key}`}
              emptyTitle={`Nothing to show in ${current?.title.toLowerCase() ?? 'this report'}`}
            />
          )}
        </SectionCard>
      </div>
    </PageRoot>
  );
}
